// Minimal single-stroke silkscreen font + terminal label placement.
// Gerber has no native text; letters are drawn as stroked polylines. v1 covers
// only the phase letters (U..Z) and labels each phase's drive terminal.
// Spec: 2026-07-06 silkscreen v1.
//
// 6-lead termination variant (2026-07-15): 'return' pads get a compound
// two-glyph label — the phase letter plus a stroked minus (U+2212) — placed
// with the same outward-offset/board-fit discipline as the single-glyph
// drive labels. The single-glyph (drive) path's arithmetic is left bit-for-
// bit unchanged (this is the general n=1 case of the same formula) so the
// wye/delta byte-stability goldens are unaffected.

import { TerminalRole } from './pcb-stator-geometry';

export type Polyline = Array<[number, number]>;   // points in a unit em box (x,y ∈ [0,1], y up)

/** Squared single-stroke capitals for the phase letters, plus the polarity
 *  signs used to compose pad labels in 6-lead independent mode: the minus
 *  (U+2212 MINUS SIGN — matches `computeReturnPads`' return-pad label glyph
 *  exactly, not a hyphen-minus) for 'return' pads, and the plus cross for
 *  the mode-gated drive-pad suffix. */
export const SILK_GLYPHS: Record<string, Polyline[]> = {
  U: [[[0, 1], [0, 0], [1, 0], [1, 1]]],
  V: [[[0, 1], [0.5, 0], [1, 1]]],
  W: [[[0, 1], [0.25, 0], [0.5, 0.6], [0.75, 0], [1, 1]]],
  X: [[[0, 1], [1, 0]], [[0, 0], [1, 1]]],
  Y: [[[0, 1], [0.5, 0.5], [1, 1]], [[0.5, 0.5], [0.5, 0]]],
  Z: [[[0, 1], [1, 1], [0, 0], [1, 0]]],
  '−': [[[0, 0.5], [1, 0.5]]],
  '+': [[[0, 0.5], [1, 0.5]], [[0.5, 0], [0.5, 1]]],
};

export const SILK_STROKE_MM = 0.15;
const GLYPH_H = 1.8;
const GLYPH_W = GLYPH_H * 0.7;
const GAP = 0.3;
const CHAR_GAP = 0.3;      // gap between glyphs within a compound (return) label
const HALF_W = GLYPH_W / 2;
const HALF_H = GLYPH_H / 2;
const EDGE_KEEPOUT = 0.1;

/** Max distance from board origin of any of the label box's 4 corners, given the
 *  box center (cx,cy) and its board-aligned half-extents. Used to test on-board fit
 *  without assuming the naive (and, near the rim, too pessimistic) cx/cy + halfH bound.
 *  `halfW` defaults to a single glyph's half-width (the drive-label case); compound
 *  (return) labels pass their wider half-width explicitly. */
function maxCornerRadius(cx: number, cy: number, halfW: number = HALF_W): number {
  let maxR = 0;
  for (const dx of [-halfW, halfW]) {
    for (const dy of [-HALF_H, HALF_H]) {
      maxR = Math.max(maxR, Math.hypot(cx + dx, cy + dy));
    }
  }
  return maxR;
}

export interface SilkSegment { x1: number; y1: number; x2: number; y2: number; }
export interface SilkLabel { text: string; phase: number; cx: number; cy: number; segments: SilkSegment[]; }
export interface SilkParams {
  termPadMm: number;
  outerRadius: number;
  /** When true, 'drive'-role terminals get a '+'-suffixed compound label
   *  ('U+' beside 'U−') — the 6-lead independent mode's symmetric labeling.
   *  Off (undefined) for wye/delta so their F_Silk stays byte-identical. */
  suffixDrive?: boolean;
}

/** One silk label per terminal, placed just radially outward from its pad.
 *  If the fully-clear offset would run the glyph off the board edge, the label is
 *  pulled inward just enough to fit (still outward of the pad); only if even the
 *  minimal outward position can't fit does it fall back to inward of the pad.
 *
 *  Terminals with `role: 'return'` get a compound two-glyph label (phase letter +
 *  minus); `role: 'drive'` gets letter + plus when `p.suffixDrive` is set (6-lead
 *  independent mode); all others (drive without the flag, or role omitted) get the
 *  single phase-letter glyph — the original v1 behavior, unchanged. */
export function computeSilkLabels(
  terminals: Array<{ x: number; y: number; phase: number; role?: TerminalRole }>,
  phaseLabels: readonly string[],
  p: SilkParams,
): SilkLabel[] {
  const out: SilkLabel[] = [];
  const fitLimit = p.outerRadius - EDGE_KEEPOUT;
  for (const t of terminals) {
    const letter = phaseLabels[t.phase] ?? String(t.phase);
    if (!SILK_GLYPHS[letter]) continue;
    const chars =
      t.role === 'return' ? [letter, '−'] :
      (p.suffixDrive && t.role === 'drive') ? [letter, '+'] :
      [letter];
    const text = chars.join('');
    const n = chars.length;
    const labelHalfW = (n * GLYPH_W + (n - 1) * CHAR_GAP) / 2;

    const padR = Math.hypot(t.x, t.y);
    const ang = Math.atan2(t.y, t.x);
    const cosA = Math.cos(ang), sinA = Math.sin(ang);
    const clearance = p.termPadMm / 2 + GAP;
    // A compound label is wider than a single glyph, so it needs extra radial push to
    // keep clear of a tangentially-close neighbor pad (e.g. a return pad's drive
    // sibling, offset by only `returnPadOffsetRad`) — not just the board edge. Zero
    // for n===1 (labelHalfW === HALF_W exactly), so the drive-label formula/output is
    // untouched bit-for-bit.
    const extraPush = Math.max(0, labelHalfW - HALF_W);
    const desiredRc = padR + clearance + HALF_H + extraPush;
    const minOutwardRc = padR + clearance;

    let rc: number;
    if (maxCornerRadius(desiredRc * cosA, desiredRc * sinA, labelHalfW) <= fitLimit) {
      rc = desiredRc;
    } else if (maxCornerRadius(minOutwardRc * cosA, minOutwardRc * sinA, labelHalfW) <= fitLimit) {
      // Pull inward just enough to clear the board edge, via binary search — still
      // outward of the pad (rc stays > padR).
      let lo = minOutwardRc, hi = desiredRc;
      for (let i = 0; i < 30; i++) {
        const mid = (lo + hi) / 2;
        if (maxCornerRadius(mid * cosA, mid * sinA, labelHalfW) <= fitLimit) lo = mid; else hi = mid;
      }
      rc = lo;
    } else {
      // Can't fit outward at all (tight rim) — fall back to inward of the pad.
      rc = padR - clearance - HALF_H;
    }
    const cx = rc * cosA, cy = rc * sinA;
    const segments: SilkSegment[] = [];
    for (let ci = 0; ci < n; ci++) {
      const glyph = SILK_GLYPHS[chars[ci]];
      if (!glyph) continue;
      // charCx: for n===1, (ci - (n-1)/2) === 0 exactly, so charCx === cx bit-for-bit
      // — the single-glyph (drive) case reduces to the original v1 formula verbatim.
      const charCx = cx + (ci - (n - 1) / 2) * (GLYPH_W + CHAR_GAP);
      const map = ([ux, uy]: [number, number]): [number, number] =>
        [charCx + (ux - 0.5) * GLYPH_W, cy + (uy - 0.5) * GLYPH_H];
      for (const poly of glyph) {
        for (let i = 1; i < poly.length; i++) {
          const [x1, y1] = map(poly[i - 1]);
          const [x2, y2] = map(poly[i]);
          segments.push({ x1, y1, x2, y2 });
        }
      }
    }
    out.push({ text, phase: t.phase, cx, cy, segments });
  }
  return out;
}

// ── Stacking-tabs ear marks (Task 5) ────────────────────────────────────────
// Each ear (`pcb-stator-stacking-tabs.ts`'s StackingEar) carries its own
// compound label ('U+' | 'U−' | ...) already, but `computeSilkLabels` above
// deliberately excludes `ear: true` terminals from the entry-pad glyph path —
// ears sit beyond the board edge on their own hole geometry, not beside a
// drive/return pad, so the pad-hugging fit logic above does not apply. This
// gives every ear its own minimal mark instead: a short radial registration
// tick (the "stage-mating" alignment cue — same physical role for the
// operator as the notch on a keyed connector) plus ONE compound label, placed
// once per ear (not once per hole — the three joint holes are a single net
// with a single identity).

const EAR_TICK_MM = 1.5;          // registration-tick length beyond the ear's centre hole
const EAR_LABEL_GAP_MM = 0.6;     // gap from the tick tip to the label's inner edge

export interface EarSilkMark {
  phase: number;
  polarity: 'plus' | 'minus';
  label: string;
  tick: SilkSegment;
  labelSegments: SilkSegment[];
}

/** Minimal shape `computeEarSilkMarks` needs from a StackingEar — avoids a
 *  hard dependency on `pcb-stator-stacking-tabs.ts`'s full interface (this
 *  module has otherwise never imported from it). */
export interface EarSilkMarkInput {
  phase: number;
  polarity: 'plus' | 'minus';
  azimuthRad: number;
  holes: ReadonlyArray<readonly [number, number]>;
  label: string;
}

export function computeEarSilkMarks(
  ears: ReadonlyArray<EarSilkMarkInput>,
  phaseLabels: readonly string[],
): EarSilkMark[] {
  return ears.map(ear => {
    const [hx, hy] = ear.holes[1];    // centre hole — the one net all three holes share
    const a = ear.azimuthRad;
    const cosA = Math.cos(a), sinA = Math.sin(a);
    const rHole = Math.hypot(hx, hy);

    const tick: SilkSegment = {
      x1: hx, y1: hy,
      x2: (rHole + EAR_TICK_MM) * cosA, y2: (rHole + EAR_TICK_MM) * sinA,
    };

    const letter = phaseLabels[ear.phase] ?? String(ear.phase);
    const chars = [letter, ear.polarity === 'plus' ? '+' : '−'];
    const n = chars.length;
    const rc = rHole + EAR_TICK_MM + EAR_LABEL_GAP_MM + HALF_H;
    const cx = rc * cosA, cy = rc * sinA;

    const labelSegments: SilkSegment[] = [];
    for (let ci = 0; ci < n; ci++) {
      const glyph = SILK_GLYPHS[chars[ci]];
      if (!glyph) continue;
      const charCx = cx + (ci - (n - 1) / 2) * (GLYPH_W + CHAR_GAP);
      const map = ([ux, uy]: [number, number]): [number, number] =>
        [charCx + (ux - 0.5) * GLYPH_W, cy + (uy - 0.5) * GLYPH_H];
      for (const poly of glyph) {
        for (let i = 1; i < poly.length; i++) {
          const [x1, y1] = map(poly[i - 1]);
          const [x2, y2] = map(poly[i]);
          labelSegments.push({ x1, y1, x2, y2 });
        }
      }
    }
    return { phase: ear.phase, polarity: ear.polarity, label: ear.label, tick, labelSegments };
  });
}
